import React, { useState, useEffect } from 'react';
import { X, QrCode, ShieldAlert, Sparkles, UserCheck } from 'lucide-react';
import { useToast } from '../shared/ToastNotification';
import { getAPIRoot } from '../../services/api/core';

export default function FastLaneCheckinModal({ isOpen, onClose, venueId }) {
  const { showSuccess, showError, showWarning } = useToast();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [guest, setGuest] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setCode('');
      setGuest(null);
      setError('');
    }
  }, [isOpen]);

  const callCheckin = async (action) => {
    const body = { code, action };
    if (action === 'confirm') {
      if (!venueId) {
        showWarning('Venue context is missing. Open this from your venue dashboard.');
        return;
      }
      body.venue_id = venueId;
    }
    const res = await fetch(`${getAPIRoot()}/passport/checkin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok || !data?.success) {
      throw new Error(data?.error || 'Check-in failed');
    }
    return data;
  };

  const handleLookup = async () => {
    if (!code || code.length < 6) {
      showWarning('Please enter a valid 6-digit STAGSTER code');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await callCheckin('verify');
      setGuest(data.guest);
      showSuccess(`Verified STAGSTER Passport code #${data.guest.code}`);
    } catch (e) {
      setError(e.message || 'No member found for this code.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!guest) return;
    setConfirming(true);
    try {
      await callCheckin('confirm');
      showSuccess(`Guest ${guest.name} checked in via FastLane!`);
      onClose();
    } catch (e) {
      showError(e.message || 'Failed to record check-in.');
    } finally {
      setConfirming(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div onClick={onClose} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fadeIn">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-slate-900 text-white rounded-3xl p-6 border border-slate-800 space-y-5 shadow-2xl relative text-left">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-xl bg-slate-800 text-slate-400 hover:text-white transition">
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 border-b border-slate-800 pb-3 pr-8">
          <div className="w-10 h-10 rounded-2xl bg-purple-600/20 text-purple-400 border border-purple-500/30 flex items-center justify-center font-bold">
            <QrCode className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-base font-extrabold text-white">FastLane QR & 6-Digit Check-In</h3>
            <p className="text-xs text-slate-400">Scan guest QR badge or enter 6-digit code at entry.</p>
          </div>
        </div>

        {/* 6-Digit Code Input Bar */}
        <div className="space-y-2">
          <label className="block text-[11px] font-bold text-purple-300 uppercase tracking-wider">Enter Guest 6-Digit Code</label>
          <div className="flex gap-2">
            <input
              type="text"
              maxLength="6"
              placeholder="e.g. 482910"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => { if (e.key === 'Enter') handleLookup(); }}
              className="flex-1 px-4 py-3 bg-slate-950 border border-slate-800 rounded-xl font-mono text-xl font-black text-amber-300 tracking-widest text-center focus:outline-none focus:border-purple-500"
            />
            <button
              onClick={handleLookup}
              disabled={loading}
              className="px-5 py-3 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-bold text-xs uppercase tracking-wider transition shadow-md shrink-0 flex items-center gap-1.5 disabled:opacity-50"
            >
              {loading ? 'Searching...' : 'Lookup'}
            </button>
          </div>
        </div>

        {error && (
          <div className="p-3 rounded-xl bg-rose-950/60 border border-rose-700/50 text-rose-200 text-xs flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 shrink-0" /> {error}
          </div>
        )}

        {/* Verified Guest Card Result */}
        {guest && (
          <div className="p-5 rounded-2xl bg-gradient-to-br from-purple-950/60 to-slate-900 border border-purple-500/30 space-y-4 shadow-lg animate-fadeIn">
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="text-base font-black text-white">{guest.name}</h4>
                  <span className="px-2 py-0.5 rounded text-[9px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30">{guest.vipStatus}</span>
                </div>
                <div className="text-xs text-slate-400 mt-0.5">{guest.email || ''} {guest.visitStreak ? `• ${guest.visitStreak} visits` : ''}</div>
              </div>
              <span className="font-mono text-xs font-bold text-purple-300 bg-purple-900/40 px-2.5 py-1 rounded border border-purple-700/40">#{guest.code}</span>
            </div>

            {/* Guest Preferences for Staff */}
            <div className="p-3 rounded-xl bg-slate-950/80 border border-slate-800 space-y-1.5 text-xs">
              <div className="font-bold text-purple-300 flex items-center gap-1 text-[11px] uppercase tracking-wider">
                <Sparkles className="w-3.5 h-3.5 text-amber-400" /> Guest Profile
              </div>
              <div className="text-slate-300 grid grid-cols-2 gap-1.5 pt-1">
                <div>City: <strong className="text-emerald-400">{guest.city || 'Not set'}</strong></div>
                <div>Vibe: <strong className="text-purple-300">{guest.preferences?.vibe || 'Not set'}</strong></div>
              </div>
            </div>

            <button
              onClick={handleConfirm}
              disabled={confirming}
              className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs uppercase tracking-wider transition shadow-md flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <UserCheck className="w-4 h-4" /> {confirming ? 'Recording...' : 'Confirm FastLane Check-In'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
